// Noticing that this browser's session was ended somewhere else.
//
// A page load reads the stored session and, while its token has not expired, never asks the auth
// server about it. Database reads only check the token's signature, so they go on working; anything
// that asks the auth server who the caller is (the API's `getUser`) gets nobody. The result was a
// tab that looked signed in while its spaces list said "No spaces yet" (2026-10-08). So the tab asks
// the auth server once after it loads, and again when it comes back to the front, and a session the
// server no longer has is signed out here.

// The answers that mean the session is gone for good. Anything else — no network, a timeout, a 5xx —
// says nothing about the session, and signing someone out over it would be worse than the bug.
const DEAD_SESSION_CODES = new Set(['session_not_found', 'user_not_found', 'refresh_token_not_found', 'bad_jwt']);

/** True when an auth error means the server has no such session (or no such user) any more. */
export function isDeadSessionError(error) {
  return !!error && DEAD_SESSION_CODES.has(error.code);
}

/**
 * Runs `check` a few seconds after load, then whenever the tab comes back to the front, at most once
 * per `minGapMs`. The delay keeps it out of the boot path, where auth's single lock is busiest.
 * Returns a remover.
 */
export function installSessionCheck(check, { win = typeof window === 'undefined' ? null : window, delayMs = 4000, minGapMs = 5 * 60 * 1000, now = () => Date.now() } = {}) {
  if (!win?.document) return () => {};
  let last = -Infinity;
  const run = () => {
    if (now() - last < minGapMs) return;
    last = now();
    Promise.resolve().then(check).catch(() => { /* a failed check is not a dead session */ });
  };
  const onVisible = () => { if (win.document.visibilityState === 'visible') run(); };
  const timer = win.setTimeout(run, delayMs);
  win.document.addEventListener('visibilitychange', onVisible);
  return () => { win.clearTimeout(timer); win.document.removeEventListener('visibilitychange', onVisible); };
}
